// src/components/health/dashboard/HealthQuickStats.tsx
// Quick Statistics Overview Component

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Calendar,
  Pill,
  Heart,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HealthLogStats } from '@/features/health/types/healthTypes/healthTypes';

// ============================================================================
// Types
// ============================================================================

interface HealthQuickStatsProps {
  stats: HealthLogStats;
  childId: string;
}

interface StatCardProps {
  label: string;
  value: string | number;
  change?: {
    value: number;
    label: string;
  };
  icon: React.ReactNode;
  color: 'sage' | 'terracotta' | 'navy' | 'success';
  href?: string;
  highlight?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const HealthQuickStats: React.FC<HealthQuickStatsProps> = ({
  stats,
  childId,
}) => {
  const statCards: StatCardProps[] = [
    {
      label: 'Total Entries',
      value: stats.totalLogs || 0,
      change: stats.comparisonPeriod ? {
        value: calculatePercentChange(
          stats.totalLogs || 0,
          stats.comparisonPeriod.totalLogs || 0
        ),
        label: 'vs last period',
      } : undefined,
      icon: <Activity className="w-5 h-5" />,
      color: 'sage',
      href: `/family/children/${childId}/health/logs`,
    },
    {
      label: 'Seizure-Free Days',
      value: stats.seizureFreeDays || 0,
      highlight: (stats.seizureFreeDays || 0) >= 7,
      icon: <Zap className="w-5 h-5" />,
      color: (stats.seizureFreeDays || 0) >= 7 ? 'success' : 'terracotta',
      href: `/family/children/${childId}/health/seizures`,
    },
    {
      label: 'Therapy Sessions',
      value: stats.byType?.therapy || 0,
      change: stats.comparisonPeriod?.byType?.therapy ? {
        value: calculatePercentChange(
          stats.byType?.therapy || 0,
          stats.comparisonPeriod.byType.therapy || 0
        ),
        label: 'vs last period',
      } : undefined,
      icon: <Heart className="w-5 h-5" />,
      color: 'sage',
      href: `/family/children/${childId}/health/therapy`,
    },
    {
      label: 'Avg per Day',
      value: (stats.averagePerDay || 0).toFixed(1),
      icon: <Calendar className="w-5 h-5" />,
      color: 'navy',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <StatCard key={stat.label} {...stat} index={index} />
      ))}
    </div>
  );
};

// ============================================================================
// Stat Card Component
// ============================================================================

const StatCard: React.FC<StatCardProps & { index: number }> = ({
  label,
  value,
  change,
  icon,
  color,
  href,
  highlight,
  index,
}) => {
  const colorClasses = {
    sage: 'bg-sage/10 text-sage-dark',
    terracotta: 'bg-terracotta/10 text-terracotta',
    navy: 'bg-navy/10 text-navy',
    success: 'bg-success/10 text-success',
  };

  const content = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className={`
        relative p-5 bg-white rounded-xl border border-cream-dark
        ${href ? 'hover:border-sage-light hover:shadow-soft cursor-pointer' : ''}
        transition-all duration-200
        ${highlight ? 'ring-2 ring-success/30' : ''}
      `}
    >
      {/* Highlight Badge */}
      {highlight && (
        <div className="absolute -top-2 -right-2 bg-success text-white text-xs font-semibold px-2 py-0.5 rounded-full">
          🎉
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        {change && (
          <TrendIndicator value={change.value} />
        )}
      </div>

      <div className="space-y-1">
        <p className="text-2xl font-display font-semibold text-navy">
          {value}
        </p>
        <p className="text-sm text-warm-gray">{label}</p>
      </div>

      {change && (
        <p className="text-xs text-warm-gray mt-2">
          <span className={change.value >= 0 ? 'text-success' : 'text-terracotta'}>
            {change.value > 0 ? '+' : ''}{change.value}%
          </span>
          {' '}{change.label}
        </p>
      )}
    </motion.div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return content;
};

// ============================================================================
// Trend Indicator
// ============================================================================

interface TrendIndicatorProps {
  value: number;
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({ value }) => {
  if (value > 5) {
    return (
      <div className="flex items-center gap-1 text-success text-xs font-medium">
        <TrendingUp className="w-3 h-3" />
        <span>+{value}%</span>
      </div>
    );
  }
  
  if (value < -5) {
    return (
      <div className="flex items-center gap-1 text-terracotta text-xs font-medium">
        <TrendingDown className="w-3 h-3" />
        <span>{value}%</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-warm-gray text-xs font-medium">
      <Minus className="w-3 h-3" />
      <span>Stable</span>
    </div>
  );
};

// ============================================================================
// Helper Functions
// ============================================================================

function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

export default HealthQuickStats;

